import {
  ZadImportCategory,
  ZadImportPayload,
  ZadImportProductType,
} from '../../../models/zad'

export type ZadImportParseErrorCode =
  | 'EMPTY_FILE'
  | 'UNSUPPORTED_FILE'
  | 'INVALID_JSON'
  | 'INVALID_ROOT'
  | 'NOTHING_TO_IMPORT'
  | 'INVALID_FIELD'

export type ZadImportParseResult =
  | {ok: true; payload: ZadImportPayload; fileName: string}
  | {ok: false; errorCode: ZadImportParseErrorCode}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

export function isSupportedZadImportFile(file: {name?: string; type?: string}): boolean {
  const name = String(file.name || '').toLowerCase()
  if (
    name.endsWith('.csv') ||
    name.endsWith('.xlsx') ||
    name.endsWith('.xls') ||
    name.endsWith('.xml')
  ) {
    return false
  }
  if (name.endsWith('.json')) return true
  return file.type === 'application/json'
}

export function formatZadImportFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function sanitizeZadImportCategory(row: unknown): ZadImportCategory {
  const source = isPlainObject(row) ? row : {}
  const category: ZadImportCategory = {
    name: typeof source.name === 'string' ? source.name : '',
    code: typeof source.code === 'string' ? source.code : '',
  }
  const description = optionalString(source.description)
  const icon = optionalString(source.icon)
  if (description !== undefined) category.description = description
  if (icon !== undefined) category.icon = icon
  return category
}

export function sanitizeZadImportProductType(row: unknown): ZadImportProductType {
  const source = isPlainObject(row) ? row : {}
  const productType: ZadImportProductType = {
    name: typeof source.name === 'string' ? source.name : '',
    code: typeof source.code === 'string' ? source.code : '',
    categoryCode: typeof source.categoryCode === 'string' ? source.categoryCode : '',
  }
  const description = optionalString(source.description)
  const icon = optionalString(source.icon)
  if (description !== undefined) productType.description = description
  if (icon !== undefined) productType.icon = icon
  return productType
}

export function sanitizeZadImportPayload(raw: unknown): ZadImportPayload {
  const source = isPlainObject(raw) ? raw : {}
  const payload: ZadImportPayload = {}
  if (Array.isArray(source.categories)) {
    payload.categories = source.categories.map(sanitizeZadImportCategory)
  }
  if (Array.isArray(source.productTypes)) {
    payload.productTypes = source.productTypes.map(sanitizeZadImportProductType)
  }
  return payload
}

export function parseZadImportJson(text: string, fileName = ''): ZadImportParseResult {
  if (text == null || String(text).trim() === '') {
    return {ok: false, errorCode: 'EMPTY_FILE'}
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return {ok: false, errorCode: 'INVALID_JSON'}
  }

  if (!isPlainObject(parsed)) {
    return {ok: false, errorCode: 'INVALID_ROOT'}
  }

  if (parsed.categories !== undefined && !Array.isArray(parsed.categories)) {
    return {ok: false, errorCode: 'INVALID_FIELD'}
  }
  if (parsed.productTypes !== undefined && !Array.isArray(parsed.productTypes)) {
    return {ok: false, errorCode: 'INVALID_FIELD'}
  }

  if (parsed.categories === undefined && parsed.productTypes === undefined) {
    return {ok: false, errorCode: 'NOTHING_TO_IMPORT'}
  }

  const payload = sanitizeZadImportPayload(parsed)
  const categoryCount = payload.categories?.length || 0
  const productTypeCount = payload.productTypes?.length || 0
  if (categoryCount === 0 && productTypeCount === 0) {
    return {ok: false, errorCode: 'NOTHING_TO_IMPORT'}
  }

  return {ok: true, payload, fileName}
}

export function getZadImportPreviewCounts(payload: ZadImportPayload) {
  const categories = payload.categories?.length || 0
  const productTypes = payload.productTypes?.length || 0
  return {categories, productTypes, total: categories + productTypes}
}

export function readZadImportFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(reader.error || new Error('UNREADABLE'))
    reader.readAsText(file)
  })
}
